import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "@reach/router";
import { Link, useTranslation } from "gatsby-plugin-react-i18next";

import styles from "./CookieConsent.module.scss";
import {
  COOKIE_CONSENT_EVENT,
  COOKIE_PREFERENCES_EVENT,
  applyConsentSideEffects,
  getDefaultConsent,
  getStoredConsent,
  openCookiePreferences,
  saveConsent,
} from "@utils/cookieConsent";
import { isBrowser } from "@utils/isBrowser";

const CookieConsent = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const [hasMounted, setHasMounted] = useState(false);
  const [hasStoredChoice, setHasStoredChoice] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [consent, setConsent] = useState(getDefaultConsent());

  const cookieOptions = useMemo(
    () => [
      {
        key: "necessary",
        required: true,
        title: t("COOKIE_CONSENT.NECESSARY_TITLE"),
        description: t("COOKIE_CONSENT.NECESSARY_DESCRIPTION"),
      },
      {
        key: "functional",
        title: t("COOKIE_CONSENT.FUNCTIONAL_TITLE"),
        description: t("COOKIE_CONSENT.FUNCTIONAL_DESCRIPTION"),
      },
      {
        key: "analytics",
        title: t("COOKIE_CONSENT.ANALYTICS_TITLE"),
        description: t("COOKIE_CONSENT.ANALYTICS_DESCRIPTION"),
      },
    ],
    [t]
  );

  const closeModal = () => setIsModalOpen(false);

  const persistConsent = nextConsent => {
    const previousConsent = getStoredConsent() || getDefaultConsent();
    const savedConsent = saveConsent(nextConsent);

    applyConsentSideEffects(previousConsent, savedConsent);
    setConsent(savedConsent);
    setHasStoredChoice(true);
    setIsModalOpen(false);
  };

  const handleRejectOptional = () => {
    persistConsent({
      functional: false,
      analytics: false,
      marketing: false,
    });
  };

  const handleAcceptAll = () => {
    persistConsent({
      functional: true,
      analytics: true,
      marketing: false,
    });
  };

  const handleSaveSelection = () => {
    persistConsent(consent);
  };

  useEffect(() => {
    if (!isBrowser()) {
      return undefined;
    }

    const storedConsent = getStoredConsent();

    setConsent(storedConsent || getDefaultConsent());
    setHasStoredChoice(Boolean(storedConsent));
    setHasMounted(true);

    const handleOpenPreferences = () => {
      setConsent(getStoredConsent() || getDefaultConsent());
      setIsModalOpen(true);
    };

    const handleConsentChange = event => {
      if (event?.detail) {
        setConsent(event.detail);
        setHasStoredChoice(true);
      }
    };

    window.addEventListener(COOKIE_PREFERENCES_EVENT, handleOpenPreferences);
    window.addEventListener(COOKIE_CONSENT_EVENT, handleConsentChange);

    return () => {
      window.removeEventListener(
        COOKIE_PREFERENCES_EVENT,
        handleOpenPreferences
      );
      window.removeEventListener(COOKIE_CONSENT_EVENT, handleConsentChange);
    };
  }, []);

  useEffect(() => {
    if (!isBrowser()) {
      return undefined;
    }

    document.body.classList.toggle("overflow-hidden", isModalOpen);

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [isModalOpen]);

  useEffect(() => {
    setIsModalOpen(false);
  }, [location.pathname]);

  if (!hasMounted) {
    return null;
  }

  return (
    <>
      {!hasStoredChoice ? (
        <aside
          className={styles.banner}
          role='dialog'
          aria-live='polite'
          aria-label={t("COOKIE_CONSENT.TITLE")}
        >
          <div className={styles.bannerCopy}>
            <span className={styles.kicker}>
              {t("COOKIE_CONSENT.KICKER")}
            </span>
            <h3>{t("COOKIE_CONSENT.TITLE")}</h3>
            <p>{t("COOKIE_CONSENT.DESCRIPTION")}</p>
            <p className={styles.bannerNote}>
              {t("COOKIE_CONSENT.CHOICE_RETENTION")}
            </p>
          </div>
          <div className={styles.bannerActions}>
            <button
              type='button'
              className={styles.secondaryButton}
              onClick={handleRejectOptional}
            >
              {t("COOKIE_CONSENT.REJECT_OPTIONAL")}
            </button>
            <button
              type='button'
              className={styles.secondaryButton}
              onClick={() => setIsModalOpen(true)}
            >
              {t("COOKIE_CONSENT.CONFIGURE")}
            </button>
            <button
              type='button'
              className={styles.primaryButton}
              onClick={handleAcceptAll}
            >
              {t("COOKIE_CONSENT.ACCEPT_ALL")}
            </button>
          </div>
        </aside>
      ) : null}

      <button
        type='button'
        className={styles.manageButton}
        onClick={openCookiePreferences}
      >
        {t("COOKIE_CONSENT.MANAGE")}
      </button>

      {isModalOpen ? (
        <div
          className={styles.overlay}
          role='presentation'
          onClick={closeModal}
        >
          <div
            className={styles.panel}
            role='dialog'
            aria-modal='true'
            aria-labelledby='cookie-consent-title'
            onClick={event => event.stopPropagation()}
          >
            <div className={styles.panelHeader}>
              <div>
                <span className={styles.kicker}>
                  {t("COOKIE_CONSENT.KICKER")}
                </span>
                <h2 id='cookie-consent-title'>
                  {t("COOKIE_CONSENT.SETTINGS_TITLE")}
                </h2>
              </div>
              <button
                type='button'
                className={styles.closeButton}
                onClick={closeModal}
                aria-label={t("COOKIE_CONSENT.CLOSE")}
              >
                x
              </button>
            </div>

            <p className={styles.panelDescription}>
              {t("COOKIE_CONSENT.SETTINGS_DESCRIPTION")}
            </p>

            <div className={styles.optionList}>
              {cookieOptions.map(option => (
                <div key={option.key} className={styles.optionCard}>
                  <div className={styles.optionCopy}>
                    <div className={styles.optionTitleRow}>
                      <h3>{option.title}</h3>
                      {option.required ? (
                        <span className={styles.badge}>
                          {t("COOKIE_CONSENT.ALWAYS_ACTIVE")}
                        </span>
                      ) : null}
                    </div>
                    <p>{option.description}</p>
                  </div>

                  <label className={styles.switch}>
                    <input
                      type='checkbox'
                      checked={Boolean(consent[option.key])}
                      disabled={option.required}
                      onChange={event =>
                        setConsent(currentConsent => ({
                          ...currentConsent,
                          [option.key]: event.target.checked,
                        }))
                      }
                    />
                    <span className={styles.slider} />
                  </label>
                </div>
              ))}
            </div>

            <div className={styles.legalBox}>
              <p>{t("COOKIE_CONSENT.LEGAL_NOTE")}</p>
              <Link to='/cookies' onClick={closeModal}>
                {t("COOKIE_CONSENT.POLICY_LINK")}
              </Link>
            </div>

            <div className={styles.panelActions}>
              <button
                type='button'
                className={styles.secondaryButton}
                onClick={handleRejectOptional}
              >
                {t("COOKIE_CONSENT.REJECT_OPTIONAL")}
              </button>
              <button
                type='button'
                className={styles.secondaryButton}
                onClick={handleSaveSelection}
              >
                {t("COOKIE_CONSENT.SAVE_SELECTION")}
              </button>
              <button
                type='button'
                className={styles.primaryButton}
                onClick={handleAcceptAll}
              >
                {t("COOKIE_CONSENT.ACCEPT_ALL")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default CookieConsent;
